<template>
    <main class="layout__main--narrow layout__main--no-top-padding">
        <BaseHeader
            v-if="pending || headerImage"
            :placeholder="pending"
            :image="headerImage"
        />

        <template v-if="!pending && page">
            <BaseSection>
                <template #title>
                    <h1>{{ page.title }}</h1>
                </template>
                <!-- eslint-disable vue/no-v-html -->
                <div
                    v-if="page.intro"
                    class="lead"
                    data-allow-anchor
                    v-html="page.intro"
                />
                <!-- eslint-enable vue/no-v-html -->
            </BaseSection>

            <BaseSection
                v-for="(category, index) in categories"
                :key="index"
                class="section--wide section--extra-spacing"
            >
                <template #title>
                    <h2>{{ category.title }}</h2>
                </template>
                <BaseGuidelineCards
                    v-if="guidelineCardsByType[category.id] && guidelineCardsByType[category.id].length"
                    :cards="guidelineCardsByType[category.id]"
                />
            </BaseSection>

            <BaseSection
                v-if="guidelineCardsByType[0]"
                class="section--wide section--extra-spacing"
            >
                <template #title>
                    <h2>Other</h2>
                </template>

                <BaseGuidelineCards
                    v-if="guidelineCardsByType[0].length"
                    :cards="guidelineCardsByType[0]"
                />
            </BaseSection>

            <BaseSection
                v-if="page.imageCta && page.imageCta.length"
                class="section--wide section--extra-spacing"
            >
                <template #title>
                    <h2>{{ page.imageCta[0].title }}</h2>
                </template>

                <template
                    v-if="page.imageCta[0].image"
                    #aside
                >
                    <BaseFigure
                        :src="page.imageCta[0].image[0].large"
                        :srcset="getSrcSet(page.imageCta[0].image[0], 'textImage')"
                        sizes="(min-width: 1401px) 605px, (min-width: 993px) 430px, 100vw"
                        :alt="page.imageCta[0].image[0].alt || ''"
                    />
                </template>

                <template
                    v-if="page.imageCta[0].text"
                    #default
                >
                    <p>{{ page.imageCta[0].text }}</p>

                    <template v-if="page.imageCta[0].buttonTitle && page.imageCta[0].buttonUrl">
                        <br>
                        <BaseButton
                            class="uro-button--mobileWide"
                            element="a"
                            :href="page.imageCta[0].buttonUrl"
                        >
                            {{ page.imageCta[0].buttonTitle }}
                        </BaseButton>
                    </template>

                    <template v-if="page.imageCta[0].extra">
                        <br><br><br>
                        <div v-html="page.imageCta[0].extra" /> <!-- eslint-disable-line vue/no-v-html -->
                    </template>
                </template>
            </BaseSection>

            <template v-if="quicklinksSections">
                <BaseQuicklinkSection
                    v-for="(quicklinksSection, index) in quicklinksSections"
                    :key="`quickLinks${index}`"
                    class="section--wide section--extra-spacing"
                    :lists="quicklinksSection.lists"
                >
                    <template #title>
                        <h2>{{ quicklinksSection.title }}</h2>
                    </template>
                </BaseQuicklinkSection>
            </template>
        </template>

        <template v-else>
            <BaseSection>
                <template #title>
                    <h1><ContentPlaceholdersHeading :lines="1" /></h1>
                </template>

                <ContentPlaceholdersText
                    :lines="5"
                    class="lead"
                />
            </BaseSection>

            <BaseSection
                v-for="(category, index) in [1,2]"
                :key="index"
                class="section--wide section--extra-spacing"
            >
                <template #title>
                    <ContentPlaceholders>
                        <ContentPlaceholdersText :lines="1" />
                    </ContentPlaceholders>
                </template>

                <BaseGuidelineCards :is-loading="true" />
            </BaseSection>

            <BaseSection class="section--wide section--extra-spacing">
                <template #title>
                    <ContentPlaceholders>
                        <ContentPlaceholdersHeading :lines="1" />
                    </ContentPlaceholders>
                </template>

                <template #aside>
                    <ContentPlaceholders :rounded="true">
                        <ContentPlaceholders-img />
                    </ContentPlaceholders>
                </template>

                <ContentPlaceholders>
                    <ContentPlaceholdersText :lines="6" />
                </ContentPlaceholders>
            </BaseSection>
        </template>
    </main>
</template>

<script setup lang="ts">
import {groupBy} from 'lodash-es';
import GuidelinesOverviewQuery from '~/graphql/queries/guidelines/overview.graphql';
import useSrcSets from '~/utils/srcset';

const { activeSite } = useMultisite();
const { getSrcSet } = useSrcSets();
const { uriFromEntryFields } = useUri();

const { variables } = useCraftGraphql();

if (activeSite.value?.handle) {
    variables.site = activeSite.value?.handle;
}

const response = await useLazyAsyncQuery({
    query: GuidelinesOverviewQuery,
    variables
});

const {
    craftEntry: page,
    headerImage,
    pending,
} = usePageQuery(
    response,
    {
        entryKey: 'page',
        seoTitle: 'Guidelines',
        activeSite
    }
);

const { data: entry } = response;

const guidelines = computed(() => {
    if (!entry.value?.guidelines) {
        return [];
    }

    return entry.value?.guidelines;
});

const categories = computed(() => {
    if (!entry.value?.categories) {
        return [];
    }

    return entry.value?.categories;
});



// function categoryById(id) {
//     if (!categories.value) {
//         return null;
//     }
//     return categories.value.find(category => Number(category.id) === Number(id));
// }

const quicklinksSections = computed(() => {
    return page.value?.quickLinks?.map((section: any) => {
        return {
            title: section.title,
            lists: section.children?.map((list: any) => {
                return {
                    links: list.links?.map((link: any) => {
                        return {
                            text: link.text,
                            uri: uriFromEntryFields(link)
                        };
                    })
                };
            })
        };
    });
});

const guidelineCardsByType = computed(() => {
    const cards = guidelines?.value?.map((guideline) => {
        return {
            title: guideline.title,
            url: `/${guideline.uri}`,
            category: guideline.guidelineCategory
        };
    });

    return groupBy(cards, (item) => {
        if (item.category?.[0]?.id) {
            return item.category[0].id;
        }
        return 0;
    });
});
</script>
